import { useI18n } from '@ecom/i18n';
import { resolveErrorMessageKey } from '@ecom/i18n/errors';
import { type ReactElement, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useCreateProductMutation, useProductsQuery } from '../api';
import { ProductForm } from '../components/ProductForm';
import type { ProductFormValues } from '../schemas';

import { type ProductDetailLocationState } from './product-detail-location-state';

import { useToast } from '@/components/ui/toast/ToastProvider';

export function ProductCreatePage(): ReactElement {
  const { t } = useI18n();
  const navigate = useNavigate();
  const toast = useToast();
  const createMutation = useCreateProductMutation();
  const productsQuery = useProductsQuery();
  const [formError, setFormError] = useState<string | null>(null);

  async function onSubmit(values: ProductFormValues): Promise<void> {
    setFormError(null);
    // Captured before the mutation — the same "read the count before it
    // changes" reasoning as StorePage's `isFirstSave` and CategoryListPage's
    // `isFirstCategory`. Read-only: no query is written, no permission is
    // consulted or altered, this only decides whether the ONE-TIME "next
    // step" banner appears on the detail page the merchant lands on next.
    const isFirstProduct = (productsQuery.data ?? []).length === 0;
    try {
      const product = await createMutation.mutateAsync(values);
      toast.success(t('merchant.products.createdToast', { name: product.name }));
      const state: ProductDetailLocationState = { showInventoryNextStep: isFirstProduct };
      void navigate(`/products/${product.id}`, { state });
    } catch (error) {
      setFormError(t(resolveErrorMessageKey(error)));
    }
  }

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-6">
      <div>
        <h1 className="sk-page-title">{t('merchant.products.create.title')}</h1>
        <p className="text-sm text-inkFaint">{t('merchant.products.create.subtitle')}</p>
      </div>

      {formError !== null ? (
        <p role="alert" className="rounded-md bg-criticalSoft px-3 py-2 text-sm text-critical">
          {formError}
        </p>
      ) : null}

      <ProductForm
        onSubmit={(values) => {
          void onSubmit(values);
        }}
        isSubmitting={createMutation.isPending}
        submitLabel={t('merchant.products.create.submitLabel')}
      />
    </div>
  );
}
